/**
 * OrgContext — current active organization + memberships + switch handler.
 *
 * Phase 1 / Org Introduction P4 (2026-06-03).
 *
 * Resolves caller's org list via ``GET /api/organizations/current`` after
 * AuthContext finishes loading. Exposes:
 *  - currentOrg / orgs
 *  - switchOrg(orgId)  — calls PUT /api/organizations/current,
 *                         swaps the bearer token in localStorage, then redirects
 *                         to /projects to avoid cross-org deep-link confusion.
 *  - reloadOrgs()      — refetches after create / member-change.
 *  - markOrgNeedsRefresh() — silent refetch trigger for child panels.
 *
 * Failure modes:
 *  - 401 → AuthContext's interceptor wipes the session; we just stop trying.
 *  - empty memberships → currentOrg = null, downstream code falls back to
 *    legacy "no-org" behavior (still works because the backend treats
 *    missing claim as global query).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from './AuthContext'
import {
  organizationsAPI,
  type OrgSummary,
} from '../services/organizationsAPI'

export interface OrgContextType {
  currentOrg: OrgSummary | null
  orgs: OrgSummary[]
  loading: boolean
  error: string | null
  /** True after first non-loading resolution. Used by Welcome flow. */
  initialized: boolean
  /** True if caller has zero memberships (e.g. brand-new user). */
  hasNoOrgs: boolean
  /** True if caller has exactly one org and it is a personal org. */
  isPersonalOnly: boolean
  switchOrg: (orgId: string) => Promise<void>
  reloadOrgs: () => Promise<void>
  /**
   * Phase 2 / T4: accent color resolved from the current org's
   * ``settings.accentColor`` JSON value. ``null`` when the org has no
   * preference (caller should fall back to ``theme.palette.primary.main``).
   * Lazy-loaded via ``GET /api/organizations/{id}`` after the active org
   * stabilizes (avoids blocking the OrgSwitcher initial paint).
   */
  currentAccentColor: string | null
  /** Force-refresh the cached accent color (e.g. after settings PATCH). */
  refreshAccentColor: () => Promise<void>
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

export const useOrg = (): OrgContextType => {
  const ctx = useContext(OrgContext)
  if (!ctx) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return ctx
}

interface OrgProviderProps {
  children: ReactNode
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const { currentUser, userProfile, loading: authLoading } = useAuth()
  const [currentOrg, setCurrentOrg] = useState<OrgSummary | null>(null)
  const [orgs, setOrgs] = useState<OrgSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Phase 2 / T4 — accent color resolved from current org's settings.
  const [currentAccentColor, setCurrentAccentColor] = useState<string | null>(
    null
  )

  const email = currentUser?.email || userProfile?.email || null
  const inflightRef = useRef<Promise<void> | null>(null)
  const accentInflightRef = useRef<string | null>(null)

  const reloadOrgs = useCallback(async () => {
    if (!email) {
      setOrgs([])
      setCurrentOrg(null)
      setInitialized(true)
      return
    }
    if (inflightRef.current) {
      return inflightRef.current
    }
    setLoading(true)
    setError(null)
    const task = (async () => {
      try {
        const res = await organizationsAPI.getCurrent()
        if (!Array.isArray(res.orgs)) {
          throw new Error('Current organization response did not include orgs[]')
        }
        setOrgs(res.orgs)
        const active = res.activeOrgId
          ? res.orgs.find((o) => o.id === res.activeOrgId) || null
          : null
        setCurrentOrg(active || res.orgs[0] || null)
      } catch (e: any) {
        // 401 is handled globally by apiClient interceptor; surface other errors.
        const status = e?.response?.status ?? e?.status
        if (status !== 401) {
          console.error('OrgContext reloadOrgs failed:', e)
          setError(
            e?.userMessage ||
              e?.message ||
              'organization 목록을 불러오지 못했습니다.'
          )
        }
        setOrgs([])
        setCurrentOrg(null)
      } finally {
        setLoading(false)
        setInitialized(true)
        inflightRef.current = null
      }
    })()
    inflightRef.current = task
    return task
  }, [email])

  // Initial load after auth resolves
  useEffect(() => {
    if (authLoading) return
    if (!email) {
      setOrgs([])
      setCurrentOrg(null)
      setInitialized(true)
      return
    }
    reloadOrgs()
  }, [authLoading, email, reloadOrgs])

  // ---- Accent color (lazy fetch of full detail) ---------------------------
  // Returns a sanitized hex color (#RRGGBB / #RGB) when valid, else null.
  const sanitizeHex = (value: unknown): string | null => {
    if (typeof value !== 'string') return null
    const v = value.trim()
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return null
    return v
  }

  const fetchAccentColor = useCallback(
    async (orgId: string) => {
      if (accentInflightRef.current === orgId) return
      accentInflightRef.current = orgId
      try {
        const detail = await organizationsAPI.get(orgId)
        const raw = (detail.settings as Record<string, unknown> | undefined)?.[
          'accentColor'
        ]
        // Only commit if the active org hasn't changed in the meantime.
        if (accentInflightRef.current === orgId) {
          setCurrentAccentColor(sanitizeHex(raw))
        }
      } catch (e) {
        // Non-fatal — accent color is purely cosmetic; fall back to theme.
        if (accentInflightRef.current === orgId) {
          setCurrentAccentColor(null)
        }
      } finally {
        if (accentInflightRef.current === orgId) {
          accentInflightRef.current = null
        }
      }
    },
    []
  )

  useEffect(() => {
    if (!currentOrg) {
      setCurrentAccentColor(null)
      return
    }
    fetchAccentColor(currentOrg.id)
  }, [currentOrg, fetchAccentColor])

  const refreshAccentColor = useCallback(async () => {
    if (!currentOrg) return
    accentInflightRef.current = null
    await fetchAccentColor(currentOrg.id)
  }, [currentOrg, fetchAccentColor])

  const switchOrg = useCallback(
    async (orgId: string) => {
      const target = orgs.find((o) => o.id === orgId)
      if (!target) {
        throw new Error('대상 organization 을 찾을 수 없습니다.')
      }
      if (currentOrg?.id === orgId) {
        return
      }
      try {
        const res = await organizationsAPI.switchCurrent(orgId)
        if (res.accessToken) {
          localStorage.setItem('auth_token', res.accessToken)
        }
        setCurrentOrg(target)
        // Cross-org deep links would confuse users; bounce to projects so
        // ProjectContext rehydrates against the new org scope.
        navigate('/projects', { replace: true })
        // Reload after navigate so the bearer carries the new claim
        await reloadOrgs()
      } catch (e: any) {
        console.error('OrgContext switchOrg failed:', e)
        throw e
      }
    },
    [currentOrg?.id, navigate, orgs, reloadOrgs]
  )

  const value = useMemo<OrgContextType>(() => {
    const hasNoOrgs = initialized && orgs.length === 0 && !!email
    const isPersonalOnly =
      initialized && orgs.length === 1 && orgs[0]?.orgType === 'personal'
    return {
      currentOrg,
      orgs,
      loading,
      error,
      initialized,
      hasNoOrgs,
      isPersonalOnly,
      switchOrg,
      reloadOrgs,
      currentAccentColor,
      refreshAccentColor,
    }
  }, [
    currentOrg,
    email,
    error,
    initialized,
    loading,
    orgs,
    reloadOrgs,
    switchOrg,
    currentAccentColor,
    refreshAccentColor,
  ])

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export default OrgContext
